/**
 * Fields that should be redacted from logs (case-insensitive matching)
 */
const SENSITIVE_FIELDS = [
  // Authentication
  "authtoken",
  "token",
  "verificationcode",
  "password",
  "secret",
  // Credit Card
  "creditcardnumber",
  "creditcardcvc",
  "creditcardcvv",
  "creditcardexpmonth",
  "creditcardexpyear",
  "nameoncard",
  "cardnumber",
  "cvv",
  "cvc",
  "cvv2",
  // PII
  "phonenumber",
  "email",
  "useremail",
  "firstname",
  "userfirstname",
  "lastname",
  "userlastname",
  "dateofbirth",
  "dob",
  "ssn",
  "socialsecuritynumber",
  // Address
  "fullname",
  "addressline1",
  "addressline2",
  "country",
  "address1",
  "address2",
  "city",
  "state",
  "postalcode",
  "zipcode",
  // Referral codes (may contain sensitive info)
  "signupreferralcode",
  "statelessreferralcode",
  // Free text a patient types into a search box (names, conditions, treatments)
  "query",
] as const;

const REDACTED_VALUE = "[REDACTED]";

/**
 * UUID pattern for validation
 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Checks if a string is a valid UUID
 */
const isUuid = (value: string): boolean => {
  return UUID_PATTERN.test(value);
};

/**
 * User ID fields that should be preserved (UUIDs are not PII)
 */
const USER_ID_FIELDS = [
  "userid",
  "platformuserid",
  "platformid",
  "platformlocationid",
  "useraddressid",
  "membershipid",
  "packageid",
  "orderid",
] as const;

/**
 * Partially masks email addresses: shows first half of local part and first half of domain
 * Example: "john.doe@example.com" -> "joh***@exam***.com"
 */
const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split("@");
  if (!domain) return REDACTED_VALUE;

  // Mask second half of local part
  const localLength = localPart.length;
  const visibleLocalLength = Math.max(1, Math.floor(localLength / 2));
  const maskedLocal = localPart.substring(0, visibleLocalLength) + "***";

  // Mask second half of domain (before TLD)
  const domainParts = domain.split(".");
  if (domainParts.length < 2) {
    return `${maskedLocal}@${domain}`;
  }

  const tld = domainParts[domainParts.length - 1];
  const domainName = domainParts.slice(0, -1).join(".");
  const domainLength = domainName.length;
  const visibleDomainLength = Math.max(1, Math.floor(domainLength / 2));
  const maskedDomain = domainName.substring(0, visibleDomainLength) + "***";

  return `${maskedLocal}@${maskedDomain}.${tld}`;
};

/**
 * Partially masks phone numbers: shows first 3 digits and last 4 digits
 * Example: "+1234567890" -> "+123***-***-7890"
 */
const maskPhone = (phone: string): string => {
  // Extract digits only
  const digits = phone.replace(/\D/g, "");
  // Require at least 7 digits to show first 3 and last 4 digits
  if (digits.length < 7) return REDACTED_VALUE;

  // Keep first 3 and last 4 digits visible
  const first3 = digits.substring(0, 3);
  const last4 = digits.substring(digits.length - 4);

  // Preserve country code prefix if present
  const hasPlus = phone.includes("+");
  const prefix = hasPlus ? "+" : "";

  return `${prefix}${first3}***-***-${last4}`;
};

/**
 * Pattern-based sanitization for credit card numbers and phone numbers
 */
const sanitizeValue = (key: string, value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }

  // Credit card pattern: 4 groups of 4 digits (with optional separators)
  const creditCardPattern = /\b\d{4}[-.\s]?\d{4}[-.\s]?\d{4}[-.\s]?\d{4}\b/g;
  if (creditCardPattern.test(value)) {
    return value.replace(creditCardPattern, "****-****-****-****");
  }

  // Skip phone masking for UUIDs (they contain dashes but aren't phone numbers)
  if (isUuid(value)) {
    return value;
  }

  // Phone number pattern: (XXX) XXX-XXXX or XXX-XXX-XXXX or +1XXXXXXXXXX
  // More specific pattern to avoid matching UUIDs or other numeric sequences
  // Must have proper phone formatting (parentheses, dashes, spaces, or + prefix)
  const phonePattern = /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}(?!\d)/g;
  // Only apply phone masking if the value looks like a phone number
  // (has separators like dashes/spaces/parentheses, or starts with +)
  if (
    phonePattern.test(value) &&
    (value.includes("-") || value.includes("(") || value.includes("+") || value.includes(" "))
  ) {
    return value.replace(phonePattern, "***-***-****");
  }

  return value;
};

/**
 * Recursively sanitizes an object by redacting sensitive fields
 * @param obj - The object to sanitize
 * @param path - Internal path tracking for nested objects (used for debugging)
 * @returns A sanitized copy of the object with sensitive fields redacted
 */
export const sanitizeForLogging = (obj: unknown, path = ""): unknown => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Handle arrays
  if (Array.isArray(obj)) {
    return obj.map((item, index) => sanitizeForLogging(item, `${path}[${index}]`));
  }

  // Handle objects
  if (typeof obj === "object") {
    const sanitized: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();
      const currentPath = path ? `${path}.${key}` : key;

      // Preserve user IDs (UUIDs are not PII)
      if (USER_ID_FIELDS.includes(lowerKey as (typeof USER_ID_FIELDS)[number])) {
        sanitized[key] = value;
      }
      // Check if field name matches sensitive field list
      // Only redact if value is not null or undefined
      else if (
        value !== null &&
        value !== undefined &&
        SENSITIVE_FIELDS.includes(lowerKey as (typeof SENSITIVE_FIELDS)[number])
      ) {
        // Apply partial masking for email and phone, full redaction for others
        if (typeof value === "string") {
          if (lowerKey === "email" || lowerKey === "useremail") {
            sanitized[key] = maskEmail(value);
          } else if (lowerKey === "phonenumber") {
            sanitized[key] = maskPhone(value);
          } else {
            sanitized[key] = REDACTED_VALUE;
          }
        } else {
          sanitized[key] = REDACTED_VALUE;
        }
      } else if (typeof value === "object" && value !== null) {
        // Recursively sanitize nested objects
        sanitized[key] = sanitizeForLogging(value, currentPath);
      } else {
        // Apply pattern-based sanitization for string values
        sanitized[key] = sanitizeValue(key, value);
      }
    }

    return sanitized;
  }

  // For primitives, apply pattern-based sanitization
  return sanitizeValue("", obj);
};
